/* eslint-disable @typescript-eslint/no-unsafe-call */
import { HXKLINE_COLOR } from '../../util/const';
import { KLineData } from '../../helper/hxKLine';

export type TooltipData = {
  name?: string;
  value: string | number;
  color: string;
};

export type StockType = 'trend' | 'kline';

const STOCK_TYPE = {
  TREND: 'trend',
  KLINE: 'kline'
};

const DEFAULT_COLORS = {
  BLACK: HXKLINE_COLOR[0],
  RED: HXKLINE_COLOR[1],
  GREEN: HXKLINE_COLOR[2]
};

function getColor(source: number | string, target: number) {
  if (Number.isNaN(source)) {
    return DEFAULT_COLORS.BLACK;
  }
  let color = DEFAULT_COLORS.BLACK;
  if ((source as number) > target) {
    color = DEFAULT_COLORS.RED;
  }
  if ((source as number) === target) {
    color = DEFAULT_COLORS.BLACK;
  }
  if ((source as number) < target) {
    color = DEFAULT_COLORS.GREEN;
  }
  return color;
}

function getAveragePrice(dataList: KLineData[]): number {
  const validData = dataList.filter(item => item.close && !Number.isNaN(item.close));
  const sumClosePrice = validData.reduce((sum, cur) => sum + cur.close, 0);
  return sumClosePrice / validData.length;
}

/**
 * @param prevClose 前一天的收盘价
 * @param close 今天的收盘价
 * @returns {string} upsAndDowns 涨跌幅
 * @returns {string} upsAndDownsAmount 涨跌幅
 */
function calculateChange(
  prevClose: number,
  close: number
): { upsAndDowns: string; upsAndDownsAmount: string } {
  const upsAndDownsAmount = (close - prevClose).toFixed(2);
  const upsAndDowns = (((close - prevClose) / prevClose) * 100).toFixed(2);
  return {
    upsAndDowns: Number.isNaN(Number(upsAndDowns)) ? '--' : upsAndDowns,
    upsAndDownsAmount: Number.isNaN(Number(upsAndDownsAmount)) ? '--' : upsAndDownsAmount
  };
}

function formatDate(timestamp: number): string {
  const currentDate = new Date(timestamp);
  return `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
}

function getTooltipData(
  kLineData: KLineData,
  stockType: StockType,
  avPrice?: number
): TooltipData[] {
  const { prevClose, close, open, high, low, timestamp } = kLineData;
  const { upsAndDowns, upsAndDownsAmount } = calculateChange(+prevClose, +close);
  const currentDate = formatDate(timestamp);

  if (stockType === STOCK_TYPE.TREND) {
    return [
      { value: currentDate, color: DEFAULT_COLORS.BLACK },
      {
        name: '成交价',
        value: Number.isNaN(+close) ? '--' : Number(close).toFixed(2),
        color: getColor(+close, +prevClose)
      },
      {
        name: '涨跌幅',
        value: upsAndDowns === '--' ? upsAndDowns : `${upsAndDowns}%`,
        color: getColor(Number(upsAndDowns), 0)
      },
      { name: '涨跌额', value: upsAndDownsAmount, color: getColor(Number(upsAndDownsAmount), 0) },
      {
        name: '均价',
        value: Number.isNaN(+avPrice) ? '--' : Number(avPrice).toFixed(2),
        color: getColor(+avPrice, +prevClose)
      }
    ];
  }
  return [
    { value: currentDate, color: DEFAULT_COLORS.BLACK },
    {
      name: '收盘',
      value: Number.isNaN(+close) ? '--' : Number(close).toFixed(2),
      color: getColor(+close, +prevClose)
    },
    {
      name: '涨幅',
      value: upsAndDowns === '--' ? upsAndDowns : `${upsAndDowns}%`,
      color: getColor(Number(upsAndDowns), 0)
    },
    {
      name: '开盘',
      value: Number.isNaN(+open) ? '--' : Number(open).toFixed(2),
      color: getColor(+open, +prevClose)
    },
    {
      name: '最高',
      value: Number.isNaN(+high) ? '--' : Number(high).toFixed(2),
      color: getColor(+high, +prevClose)
    },
    {
      name: '最低',
      value: Number.isNaN(+low) ? '--' : Number(low).toFixed(2),
      color: getColor(+low, +prevClose)
    }
  ];
}

export function getHXKLineTooltipData(
  kLineIns: unknown,
  stockType: StockType,
  dataIndex?: number | undefined
): {
  defaultTooltipData: TooltipData[];
  kLineData: KLineData;
} {
  let dataList = kLineIns.getDataList() as KLineData[];
  if (dataIndex === undefined && stockType === STOCK_TYPE.TREND) {
    dataList = kLineIns.getRealDataList() as KLineData[];
  }

  // 获取今天和前一天的数据
  const prevIndex = Math.max(0, dataIndex !== undefined ? dataIndex - 1 : dataList.length - 2);
  const prevData = dataList[prevIndex];
  const curIndex = Math.max(0, dataIndex !== undefined ? dataIndex : dataList.length - 1);
  const curData = dataList[curIndex];

  /**
   * 分时与k线使用的数据来源不同，但后面处理数据方式相同，这里处理了相同的数据结构
   * @see https://khtest.10jqka.com.cn/kodexplorer/data/User/wangyifan/home/HXKlineChart/docs/guide/instance-api.html#getpreprice
   * 分时图的前一天的收盘价数据通过getPrePrice()获取
   * */
  const prevClose =
    stockType === STOCK_TYPE.TREND ? (kLineIns.getPrePrice() as number) : prevData?.close;

  /** 计算分时图的均价 */
  const avPrice = stockType === STOCK_TYPE.TREND ? getAveragePrice(dataList) : undefined;

  const kLineData = {
    ...curData,
    prevClose
  } as KLineData;

  const defaultTooltipData = getTooltipData(kLineData, stockType, avPrice);

  return { defaultTooltipData, kLineData };
}

export default getHXKLineTooltipData;
